/**
 * Settings → Sources (web.md §4; sources.md editors; design fix 3). The item column is SourceList.tsx (numbers,
 * sort, filters, pause / resume, selection); each row has the source.s kind
 * icon, name, kind and a status dot: ok (green), error (red), needs a credential (amber), paused
 * (hollow). The editor is SourceEditor.tsx.
 */

import type { SourceId, SourceStatus } from '@digester/core';
import { useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { SOURCE_KIND_LABELS, sourceKindIcon } from '../../ui/source-icons.js';
import '../rules/rules.css';
import { EditorEmpty, type ItemsResult, type SettingsCategory, type SettingsEditorProps, type SettingsItem } from '../shell/index.js';
import { healthOf, isFiltered, sourceList } from './list-model.js';
import { newSourceDraft, SourceForm, sourceDraftOf } from './SourceEditor.js';
import { SourceList } from './SourceList.js';
import { registerSourcesDialogs } from './SourcesFilterDialog.js';
import './sources.css';

export { healthOf, type SourceHealth } from './list-model.js';

function useStatuses(): SourceStatus[] {
  return useEngineQuery(qk.sourceStatus(), (e) => e.sources.status()).data ?? [];
}

function useItems(): ItemsResult {
  const q = useEngineQuery(qk.sources(), (engine) => engine.sources.list());
  const statuses = useStatuses();
  return {
    // By name: with 128 sources an id-ordered list is impossible to scan.
    items: [...(q.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map((s): SettingsItem => {
      const health = healthOf(s, statuses.find((x) => x.sourceId === s.id));
      const text = s.enabled ? (health === 'error' ? 'Last pull failed' : health === 'secret' ? 'Needs a password or token on this device' : null) : s.disabledReason === 'plan' ? 'Paused: over the plan’s source limit' : null;
      const tone = health === 'error' ? 'error' : health === 'secret' ? 'warn' : null;
      return { id: s.id, name: s.name, icon: sourceKindIcon(s.kind), subtitle: SOURCE_KIND_LABELS[s.kind], enabled: s.enabled, ...(text ? { status: text } : {}), ...(tone ? { tone } : {}) };
    }),
    isLoading: q.isLoading,
    error: q.error ?? null,
  };
}

function SourceEditor({ recordId, duplicateOf }: SettingsEditorProps) {
  const q = useEngineQuery(qk.sources(), (engine) => engine.sources.list());
  const sources = q.data ?? [];
  if (!recordId) return <EditorEmpty text="No sources yet" />;
  if (q.isLoading) return <EditorEmpty text="Loading…" />;
  if (recordId === 'new') {
    const from = duplicateOf ? sources.find((s) => s.id === duplicateOf) : undefined;
    // A duplicate copies the configuration but starts with no statistics; its address must differ.
    const initial = from ? { ...sourceDraftOf(from), name: `${from.name} copy`, address: from.kind === 'email' ? '' : sourceDraftOf(from).address } : newSourceDraft('rss');
    return <SourceForm source={null} initial={initial} />;
  }
  const source = sources.find((s) => s.id === recordId);
  if (!source) return <EditorEmpty />;
  return <SourceForm key={source.id} source={source} initial={sourceDraftOf(source)} />;
}

export const category: SettingsCategory = {
  id: 'sources',
  name: 'Sources',
  icon: 'rss',
  useItems,
  Editor: SourceEditor,
  newLabel: 'New source',
  duplicate: true,
  archive: (engine, item) => engine.sources.archive(item.id as SourceId),
  List: SourceList,
  filterDialog: { kind: 'sources-filter', subscribe: sourceList.subscribe, pressed: () => isFiltered(sourceList.get()) },
};

registerSourcesDialogs();
